import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, Timestamp, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { MunicipalityAssignment } from '@municipality/types';

interface MunicipalityAssignmentWithMunicipality extends MunicipalityAssignment {
  municipalityName?: string;
}

interface UseMunicipalityAssignmentsResult {
  assignments: MunicipalityAssignmentWithMunicipality[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch and manage municipality assignments for a specific user
 * with real-time updates
 */
export const useMunicipalityAssignments = (userId: string | null | undefined): UseMunicipalityAssignmentsResult => {
  const [assignments, setAssignments] = useState<MunicipalityAssignmentWithMunicipality[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefetchTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!userId) {
      setAssignments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Set up real-time listener for user document
    const userRef = query(
      collection(db, 'users'),
      where('__name__', '==', userId)
    );

    const unsubscribe = onSnapshot(
      userRef,
      async (snapshot) => {
        try {
          if (snapshot.empty) {
            setAssignments([]);
            setLoading(false);
            return;
          }

          const userData = snapshot.docs[0].data();
          const municipalityAssignments = (userData.municipalityAssignments || []) as MunicipalityAssignment[];

          // Fetch municipality names for each assignment
          const assignmentsWithNames = await Promise.all(
            municipalityAssignments.map(async (assignment) => {
              try {
                const municipalityQuery = query(
                  collection(db, 'municipalities'),
                  where('__name__', '==', assignment.municipalityId)
                );
                
                const municipalitySnapshot = await new Promise<QuerySnapshot<DocumentData>>((resolve) => {
                  const unsub = onSnapshot(municipalityQuery, (snap) => {
                    unsub();
                    resolve(snap);
                  });
                });

                // Extract municipality name with proper type handling
                let municipalityName = 'Unknown Municipality';
                if (!municipalitySnapshot.empty && municipalitySnapshot.docs.length > 0) {
                  const docData = municipalitySnapshot.docs[0].data();
                  municipalityName = (docData && typeof docData.name === 'string') 
                    ? docData.name 
                    : 'Unknown Municipality';
                }

                return {
                  ...assignment,
                  municipalityName,
                  // Convert Firestore Timestamps to Date objects
                  grantedAt: assignment.grantedAt instanceof Timestamp 
                    ? assignment.grantedAt.toDate() 
                    : new Date(assignment.grantedAt),
                  expiresAt: assignment.expiresAt instanceof Timestamp
                    ? assignment.expiresAt.toDate()
                    : assignment.expiresAt ? new Date(assignment.expiresAt) : undefined,
                  lastAccessed: assignment.lastAccessed instanceof Timestamp
                    ? assignment.lastAccessed.toDate()
                    : assignment.lastAccessed ? new Date(assignment.lastAccessed) : undefined,
                };
              } catch (err) {
                console.error('Error fetching municipality name:', err);
                return {
                  ...assignment,
                  municipalityName: 'Unknown Municipality',
                  grantedAt: assignment.grantedAt instanceof Timestamp 
                    ? assignment.grantedAt.toDate() 
                    : new Date(assignment.grantedAt),
                  expiresAt: assignment.expiresAt instanceof Timestamp
                    ? assignment.expiresAt.toDate()
                    : assignment.expiresAt ? new Date(assignment.expiresAt) : undefined,
                  lastAccessed: assignment.lastAccessed instanceof Timestamp
                    ? assignment.lastAccessed.toDate()
                    : assignment.lastAccessed ? new Date(assignment.lastAccessed) : undefined,
                };
              }
            })
          );

          setAssignments(assignmentsWithNames);
          setLoading(false);
        } catch (err) {
          console.error('Error processing assignments:', err);
          setError(err instanceof Error ? err.message : 'Failed to load assignments');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error listening to assignments:', err);
        setError(err.message || 'Failed to load assignments');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, refetchTrigger]);

  return {
    assignments,
    loading,
    error,
    refetch,
  };
};
